import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useEffect, useContext, useRef, useMemo } from 'react';
import Badge from '@mui/material/Badge';
import NotificationsIcon from '@mui/icons-material/Notifications';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import checkdouble from '../../public/checkdouble.svg';
import { Box, IconButton, Menu, Typography, Switch, Divider, Tooltip, Fab } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { constants } from '../constants';
import { NotificationAPI } from '../apis/NotificationAPI';
import AlertStateIcon from '../../public/AlertStateIcon.svg';
import { notificationApiError } from '@/utils/notificationApiError';
import { getNotificationIcon, groupNotifications, getTimeAgo, NOTIFICATION_POLL_INTERVAL_MS, NOTIFICATION_TITLES, getFormattedNotificationMessage } from '../utils/notificationUtils';
import { getUserFromCookie } from '@/utils/utilities';
import { ConfigContext } from './ConfigContext';
import { usePlatformHost } from '../hooks/usePlatformHost';
import Sound from './Sound/Sound';
import { PermissionGate } from './PermissionGate';
import { RBAC_ACTIONS } from '@/utils/permissionGateConfig';
import { useSurveyNotification } from '@/hooks/useSurveyNotification';
const groupLabels = {
    lastWeek: constants.LAST_WEEK,
    lastMonth: constants.LAST_MONTH,
    older: constants.OLDER_NOTIFICATIONS
};
const NotificationItem = ({ notification }) => {
    const theme = useTheme();
    const { configState } = useContext(ConfigContext);
    const buttonColor = configState?.settings?.button_color || theme.palette.primary.main;
    const textRef = useRef(null);
    const [isOverflowing, setIsOverflowing] = useState(false);
    // Get formatted message (handles upgrade/maintenance date formatting)
    const displayMessage = getFormattedNotificationMessage(notification?.title ?? '', notification?.message, notification?.scheduled, notification?.expires);
    // Check if this is a survey notification
    const isSurveyNotification = (notification?.title ?? '')
        .toLowerCase()
        .includes(NOTIFICATION_TITLES.SURVEY_AVAILABLE.toLowerCase());
    // Handle Get Started button click
    const handleGetStartedClick = () => {
        if (notification?.url) {
            window.open(notification.url, '_blank', 'noopener,noreferrer');
        }
    };
    useEffect(() => {
        const element = textRef.current;
        if (element) {
            setIsOverflowing(element.scrollHeight > element.clientHeight);
        }
    }, [displayMessage]);
    return (_jsxs(Box, { sx: {
            display: 'flex',
            alignItems: 'flex-start',
            background: notification.viewedDateTime
                ? theme.palette.background.default
                : theme.palette.background.paper,
            border: notification.viewedDateTime
                ? 'none'
                : `1px solid ${theme.palette.background.paper}`,
            borderRadius: 3,
            borderBottom: notification.viewedDateTime
                ? `1px solid ${theme.palette.divider}`
                : 'none',
            pl: 2,
            pt: 2.5,
            pb: 2.5,
            pr: 2.5,
            mb: 0.5,
            position: 'relative',
            ...(!notification.viewedDateTime && {
                '&:hover': {
                    borderRadius: '12px',
                    border: `1px solid ${theme.palette.grey[400]}`,
                    background: theme.palette.background.paper
                }
            })
        }, children: [_jsxs(Box, { sx: {
                    position: 'absolute',
                    top: 20,
                    right: 20,
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 1
                }, children: [_jsx(Typography, { variant: "caption", sx: {
                            color: theme.palette.text.secondary,
                            fontSize: 12,
                            fontWeight: 400,
                            whiteSpace: 'nowrap'
                        }, "data-testid": "typography-caption", children: getTimeAgo(notification.meta?.created) }), !notification.viewedDateTime && (_jsx(Box, { sx: {
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            background: theme.palette.error.main
                        } }))] }), _jsx(Box, { sx: { mr: 2, flexShrink: 0 }, children: getNotificationIcon(notification.title ?? '', '#000000') }), _jsxs(Box, { sx: { flex: 1, minWidth: 0 }, children: [_jsx(Typography, { variant: "subtitle2", sx: {
                            fontWeight: 600,
                            fontSize: 16,
                            color: theme.palette.text.primary,
                            pr: 8
                        }, "data-testid": "typography-subtitle2", children: notification.title }), _jsx(Tooltip, { title: isOverflowing ? displayMessage : '', arrow: true, placement: "bottom", disableHoverListener: !isOverflowing, slotProps: {
                            tooltip: {
                                sx: {
                                    maxWidth: 300,
                                    marginRight: '16px',
                                    backgroundColor: '#000000',
                                    color: '#ffffff',
                                    fontSize: '14px',
                                    fontWeight: 300,
                                    lineHeight: '16px',
                                    padding: '8px 12px',
                                    borderRadius: '4px'
                                }
                            }
                        }, children: _jsx(Typography, { ref: textRef, variant: "body2", sx: {
                                color: theme.palette.text.secondary,
                                fontSize: 14,
                                fontWeight: 400,
                                lineHeight: 1.4,
                                display: '-webkit-box',
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                cursor: 'default'
                            }, "data-testid": "typography-body2", children: displayMessage }) }), isSurveyNotification && notification.url && (_jsx(Typography, { component: "span", onClick: handleGetStartedClick, "data-testid": "get-started-button", sx: {
                            mt: 0.5,
                            color: buttonColor || theme.palette.primary.main,
                            fontSize: 14,
                            fontStyle: 'normal',
                            fontWeight: 600,
                            lineHeight: '152%',
                            letterSpacing: '0.17px',
                            cursor: 'pointer',
                            '&:hover': {
                                textDecoration: 'underline'
                            }
                        }, children: constants.get_started_button }))] })] }, notification.id));
};
// Helper: Get badge styles
const getBadgeStyles = (theme, count) => ({
    '& .MuiBadge-badge': {
        minWidth: 18,
        width: 18,
        height: 18,
        borderRadius: '50%',
        fontSize: count > 99 ? 8 : 10,
        fontWeight: 600,
        border: 'none',
        backgroundColor: theme.palette.error.main,
        color: theme.palette.common.white,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0
    }
});
const NotificationModal = () => {
    const theme = useTheme();
    const { configState } = useContext(ConfigContext);
    const getPlatformHost = usePlatformHost();
    const [platformHost, setPlatformHost] = useState('');
    useEffect(() => {
        getPlatformHost().then(setPlatformHost);
    }, [getPlatformHost]);
    // Hook for rating notification
    const { shouldShowSurveyNotification, isSurveyLoading } = useSurveyNotification();
    // State for rating notification
    const [ratingSubmitted, setRatingSubmitted] = useState(false);
    const [selectedRating, setSelectedRating] = useState(0);
    const [hoveredRating, setHoveredRating] = useState(0);
    const [isFadingRating, setIsFadingRating] = useState(false);
    const [isRatingDismissed, setIsRatingDismissed] = useState(false);
    // Determine bell icon color based on header background color
    const getBellIconColor = () => {
        const headerBgColor = configState?.settings?.header_background_color || '#fff';
        const isWhiteBackground = headerBgColor.toUpperCase() === '#FFFFFF' ||
            headerBgColor.toUpperCase() === '#FFF';
        return isWhiteBackground ? '#000000' : '#FFFFFF';
    };
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const [onlyUnread, setOnlyUnread] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    const [notifications, setNotifications] = useState([]);
    const [tipPosition, setTipPosition] = useState('50%');
    const [userId, setUserId] = useState('');
    const [error, setError] = useState(null);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [offset, setOffset] = useState(0);
    const [showScrollTop, setShowScrollTop] = useState(false);
    const scrollContainerRef = useRef(null);
    const LIMIT = 10;
    const [play, setPlay] = useState(false);
    // Set user ID and username on component mount
    useEffect(() => {
        const user = getUserFromCookie();
        setUserId(user.id);
    }, []);
    useEffect(() => {
        if (open) {
            globalThis.dispatchEvent(new Event('notificationModalOpen'));
        }
        else {
            globalThis.dispatchEvent(new Event('notificationModalClose'));
        }
    }, [open]);
    const calculateTipPosition = () => {
        if (!anchorEl)
            return;
        const badge = anchorEl.querySelector('.MuiBadge-root');
        const notificationIcon = anchorEl.querySelector('[data-testid="notifications-icon"]');
        const targetElement = badge || notificationIcon;
        if (!targetElement)
            return;
        const iconRect = targetElement.getBoundingClientRect();
        const menuPaper = document.querySelector('[data-testid="notification-menu"] .MuiPaper-root');
        if (!menuPaper)
            return;
        const menuRect = menuPaper.getBoundingClientRect();
        const iconCenterX = iconRect.left + iconRect.width / 2;
        const distanceFromMenuLeft = iconCenterX - menuRect.left;
        const percentageFromLeft = (distanceFromMenuLeft / menuRect.width) * 100;
        const clampedPercentage = Math.max(5, Math.min(95, percentageFromLeft));
        setTipPosition(`${clampedPercentage}%`);
    };
    useEffect(() => {
        if (!open || !anchorEl)
            return;
        requestAnimationFrame(() => {
            setTimeout(calculateTipPosition, 150);
        });
    }, [open, anchorEl]);
    const handleBellClick = (event) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };
    const getErrorMessage = (err) => {
        // Check for API error code first
        const errorCode = err?.response?.data?.errorCode;
        if (errorCode &&
            typeof errorCode === 'string' &&
            errorCode in notificationApiError) {
            return notificationApiError[errorCode].message;
        }
        // Map error message patterns to error types
        const errorPatterns = {
            'Network Error': notificationApiError.NETWORK_ERROR.message,
            timeout: notificationApiError.TIMEOUT.message,
            '401': notificationApiError.UNAUTHORIZED.message,
            '403': notificationApiError.FORBIDDEN.message,
            '404': notificationApiError.NOT_FOUND.message,
            'rate limit': notificationApiError.RATE_LIMITED.message,
            CORS: notificationApiError.CORS_ERROR.message,
            'Service Unavailable': notificationApiError.SERVICE_UNAVAILABLE.message
        };
        // Find matching error pattern in message
        const errorMessage = err?.message ?? '';
        const matchedPattern = Object.entries(errorPatterns).find(([pattern]) => errorMessage.includes(pattern));
        return matchedPattern?.[1] ?? notificationApiError.SERVER_ERROR.message;
    };
    const mapNotificationEntry = (entry) => {
        const resource = entry.resource;
        return {
            resourceType: resource.resourceType ?? 'Notification',
            id: resource?.id,
            to: resource?.to ?? [],
            ccTo: resource?.ccTo ?? [],
            bccTo: resource?.bccTo ?? [],
            from: resource?.from ?? { type: '', value: '' },
            title: resource?.title ?? constants.notification_default_title,
            subtitle: resource?.subtitle,
            scheduled: resource?.scheduled,
            expires: resource?.expires,
            additionalInformation: resource?.additionalInformation ?? [],
            url: resource?.url,
            messageType: resource?.messageType ?? 'Text',
            message: resource?.message ?? '',
            channels: resource?.channels ?? [],
            viewedDateTime: resource?.viewedDateTime,
            attachment: resource?.attachment
                ? {
                    contentTransferEncoding: resource.attachment.contentTransferEncoding,
                    content: resource.attachment.content,
                    contentDescription: resource.attachment.contentDescription,
                    contentDisposition: resource.attachment.contentDisposition,
                    hash: resource.attachment.hash,
                    fileName: resource.attachment.fileName,
                    creation: resource.attachment.creation
                }
                : null,
            rating: resource?.rating,
            meta: {
                versionId: resource?.meta?.versionId ?? 0,
                created: resource?.meta?.created ?? '',
                lastUpdated: resource?.meta?.lastUpdated,
                createdBy: resource?.meta?.createdBy,
                lastUpdatedBy: resource?.meta?.lastUpdatedBy,
                isDeleted: resource?.meta?.isDeleted ?? false,
                deviceId: resource?.meta?.deviceId,
                clientRequestId: resource?.meta?.clientRequestId,
                requestLogRef: resource?.meta?.requestLogRef,
                source: resource?.meta?.source
            }
        };
    };
    const mergePollingNotifications = (prev, newNotifications) => {
        if (prev.length === 0) {
            return newNotifications;
        }
        const existingIds = new Set(prev.map((n) => n.id));
        const brandNewNotifications = newNotifications.filter((n) => !existingIds.has(n.id));
        const updatedExisting = prev.map((existing) => {
            const updated = newNotifications.find((n) => n.id === existing.id);
            if (!updated)
                return existing;
            // Preserve local viewedDateTime if it exists but server data doesn't have it yet
            // This prevents race condition where polling overwrites local read state with stale server data
            if (existing.viewedDateTime && !updated.viewedDateTime) {
                return { ...updated, viewedDateTime: existing.viewedDateTime };
            }
            return updated;
        });
        if (brandNewNotifications.length > 0) {
            setPlay(true);
            return [...brandNewNotifications, ...updatedExisting];
        }
        return updatedExisting;
    };
    const handleLoadMoreNotifications = (prev, newNotifications) => {
        const existingIds = new Set(prev.map((n) => n.id));
        const uniqueNew = newNotifications.filter((n) => !existingIds.has(n.id));
        const shouldHaveMore = newNotifications.length === LIMIT;
        setHasMore(shouldHaveMore);
        return [...prev, ...uniqueNew];
    };
    const handleInitialLoad = (newNotifications) => {
        const shouldHaveMore = newNotifications.length === LIMIT;
        setNotifications(newNotifications);
        setHasMore(shouldHaveMore);
    };
    const handleNotificationSuccess = (data, isMounted, isLoadMore = false, isPolling = false) => {
        if (!isMounted)
            return;
        const newNotifications = (data.entry ?? []).map(mapNotificationEntry);
        if (isLoadMore) {
            setNotifications((prev) => handleLoadMoreNotifications(prev, newNotifications));
            setIsLoadingMore(false);
            return;
        }
        if (isPolling) {
            setNotifications((prev) => mergePollingNotifications(prev, newNotifications));
            setIsLoadingMore(false);
            return;
        }
        handleInitialLoad(newNotifications);
        setIsLoadingMore(false);
    };
    const handleNotificationError = (err, isMounted) => {
        if (!isMounted)
            return;
        const message = getErrorMessage(err);
        setError(message);
    };
    const shouldLoadMore = (scrollPercentage) => {
        return scrollPercentage > 0.8 && hasMore && !isLoadingMore && !error;
    };
    const triggerLoadMore = () => {
        const newOffset = offset + LIMIT;
        setOffset(newOffset);
        const fetchFn = globalThis.fetchNotifications;
        if (fetchFn) {
            fetchFn(newOffset, true);
        }
    };
    const handleScroll = (e) => {
        const target = e.currentTarget;
        const scrollPercentage = (target.scrollTop + target.clientHeight) / target.scrollHeight;
        // Show/hide scroll-to-top button based on scroll position
        setShowScrollTop(target.scrollTop > 100);
        // Load more when scrolled 80% of the way down
        if (shouldLoadMore(scrollPercentage)) {
            triggerLoadMore();
        }
    };
    // Fetch notifications on mount and poll with backoff on failures
    useEffect(() => {
        let isMounted = true;
        let intervalId;
        let isPollingInFlight = false;
        let consecutivePollingFailures = 0;
        const MAX_CONSECUTIVE_POLLING_FAILURES = 3;
        const scheduleNextPoll = () => {
            if (intervalId)
                clearInterval(intervalId);
            if (!isMounted)
                return;
            if (consecutivePollingFailures >= MAX_CONSECUTIVE_POLLING_FAILURES) {
                return;
            }
            // Backoff: double the interval for each consecutive failure (30s → 60s → 120s)
            const delay = NOTIFICATION_POLL_INTERVAL_MS *
                Math.pow(2, consecutivePollingFailures);
            intervalId = setInterval(() => {
                fetchNotifications(0, false, true);
            }, delay);
        };
        const fetchNotifications = (currentOffset = 0, isLoadMore = false, isPolling = false) => {
            if (platformHost?.includes('https://')) {
                if (isPolling && isPollingInFlight)
                    return;
                if (isLoadMore) {
                    setIsLoadingMore(true);
                }
                else if (!isPolling) {
                    setError(null);
                }
                if (isPolling)
                    isPollingInFlight = true;
                NotificationAPI.getNotificationBundle(userId, currentOffset, LIMIT, ['pull'], platformHost)
                    .then((data) => {
                    handleNotificationSuccess(data, isMounted, isLoadMore, isPolling);
                    setUnreadCount(data.totalUnread);
                    if (isPolling && consecutivePollingFailures > 0) {
                        consecutivePollingFailures = 0;
                        scheduleNextPoll();
                    }
                })
                    .catch((err) => {
                    handleNotificationError(err, isMounted);
                    if (isPolling) {
                        consecutivePollingFailures += 1;
                        scheduleNextPoll();
                    }
                })
                    .finally(() => {
                    if (isPolling)
                        isPollingInFlight = false;
                });
            }
        };
        globalThis.fetchNotifications = fetchNotifications;
        if (!userId)
            return;
        setOffset(0);
        setHasMore(true);
        fetchNotifications(0, false, false);
        // Start polling to keep badge count updated
        scheduleNextPoll();
        return () => {
            isMounted = false;
            if (intervalId)
                clearInterval(intervalId);
        };
    }, [userId, platformHost]);
    // Filter notifications based on toggle
    const filteredNotifications = useMemo(() => onlyUnread
        ? notifications?.filter((n) => !n.viewedDateTime)
        : notifications, [onlyUnread, notifications]);
    // Group notifications
    const grouped = groupNotifications(filteredNotifications);
    const renderErrorState = () => (_jsx(Box, { sx: {
            height: 444,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            pt: 6,
            pb: 6
        }, children: _jsx(Typography, { variant: "body2", color: "error", align: "center", sx: {
                fontSize: 16,
                fontWeight: 600,
                color: theme.palette.error.main
            }, children: error }) }));
    const renderEmptyState = () => (_jsxs(Box, { sx: {
            height: 444,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            pt: 6,
            pb: 6
        }, children: [_jsx(Box, { component: "img", src: AlertStateIcon, alt: constants.NO_NOTIFICATIONS, sx: { width: 64, height: 64 } }), _jsx(Box, { sx: {
                    mt: 1,
                    pt: 1,
                    pb: 1,
                    pl: 3,
                    pr: 3,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }, children: _jsx(Typography, { variant: "body2", color: "text.secondary", align: "center", sx: {
                        fontSize: 16,
                        fontWeight: 600,
                        color: theme.palette.text.primary
                    }, children: constants.NO_NOTIFICATIONS }) })] }));
    const renderGroupLabel = (group, idx) => {
        // Don't render heading for "recent" group (last 7 days)
        if (group === 'recent') {
            return null;
        }
        const isFirstGroup = idx === 0;
        return (_jsx(Box, { sx: { mt: isFirstGroup ? '8px' : '16px', mb: '4px' }, children: _jsx(Typography, { variant: "caption", sx: {
                    color: theme.palette.text.secondary,
                    fontWeight: 600,
                    fontSize: 13,
                    pl: '12px',
                    letterSpacing: 1
                }, "data-testid": "typography-caption", children: groupLabels[group] }) }));
    };
    // Handle rating submission
    const handleRatingClick = async (rating) => {
        setSelectedRating(rating);
        // Start fading the notification after a brief moment to show filled stars
        setTimeout(() => {
            setIsFadingRating(true);
        }, 100);
        try {
            const user = getUserFromCookie();
            await NotificationAPI.submitSurveyResponse(rating, user, platformHost);
        }
        catch (err) {
            console.error('Error submitting rating:', err);
        }
        // After 1.5 seconds of fading, show thank you message
        setTimeout(() => {
            setIsFadingRating(false);
            setRatingSubmitted(true);
        }, 1600);
        // After 5 more seconds, dismiss the rating prompt
        setTimeout(() => {
            setIsRatingDismissed(true);
            setRatingSubmitted(false);
            setSelectedRating(0);
            setHoveredRating(0);
        }, 6600);
    };
    const renderStarButton = (star) => {
        const activeRating = selectedRating > 0 ? selectedRating : hoveredRating;
        const isFilled = star <= activeRating;
        const StarComponent = isFilled ? StarIcon : StarBorderIcon;
        const starColor = isFilled ? '#FFD700' : '#D1D5DB';
        return (_jsx(IconButton, { onClick: () => handleRatingClick(star), onMouseEnter: () => selectedRating === 0 &&
                setHoveredRating(star), onMouseLeave: () => selectedRating === 0 && setHoveredRating(0), sx: {
                p: 0.25,
                cursor: selectedRating > 0 ? 'default' : 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                    transform: selectedRating === 0 ? 'scale(1.1)' : 'none',
                    backgroundColor: 'transparent'
                }
            }, disabled: selectedRating > 0, children: _jsx(StarComponent, { sx: { fontSize: 20, color: starColor } }) }, star));
    };
    // Render rating notification
    const renderRatingNotification = () => {
        const RATING_STARS = [1, 2, 3, 4, 5];
        // Show thank you message if submitted
        if (ratingSubmitted) {
            return (_jsxs(Box, { sx: {
                    display: 'flex',
                    alignItems: 'center',
                    background: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.background.paper}`,
                    borderRadius: 3,
                    borderBottom: 'none',
                    pl: 2,
                    pt: 2.5,
                    pb: 2.5,
                    pr: 2.5,
                    mb: 0.5,
                    position: 'relative'
                }, children: [_jsx(Box, { sx: { mr: 2 }, children: getNotificationIcon(NOTIFICATION_TITLES.FEEDBACK, '#000000') }), _jsx(Typography, { variant: "subtitle2", sx: {
                            fontWeight: 600,
                            fontSize: 16,
                            color: theme.palette.text.primary
                        }, children: constants.notification_rating_thanks })] }, "rating-notification"));
        }
        return (_jsx(Box, { sx: {
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                background: theme.palette.background.paper,
                border: `1px solid ${theme.palette.background.paper}`,
                borderRadius: 3,
                borderBottom: 'none',
                pl: 2,
                pt: 2,
                pb: 2,
                pr: 3,
                mb: 0.5,
                position: 'relative',
                opacity: isFadingRating ? 0.3 : 1,
                transition: 'opacity 3s ease-out',
                pointerEvents: isFadingRating ? 'none' : 'auto',
                ...(!isFadingRating && {
                    '&:hover': {
                        borderRadius: '12px',
                        border: `1px solid #F5F5F5`,
                        background: '#FFF'
                    }
                })
            }, children: _jsxs(Box, { sx: {
                    display: 'flex',
                    alignItems: 'flex-start',
                    width: '100%'
                }, children: [_jsx(Box, { sx: { mr: 2 }, children: getNotificationIcon(NOTIFICATION_TITLES.FEEDBACK, '#000000') }), _jsxs(Box, { sx: { flex: 1 }, children: [_jsx(Typography, { variant: "subtitle2", sx: {
                                    fontWeight: 600,
                                    fontSize: 16,
                                    color: theme.palette.text.primary,
                                    mb: 1
                                }, children: NOTIFICATION_TITLES.FEEDBACK }), _jsx(Box, { sx: {
                                    display: 'flex',
                                    gap: 0.5,
                                    alignItems: 'center'
                                }, children: RATING_STARS.map((star) => renderStarButton(star)) })] })] }) }, "rating-notification"));
    };
    const renderNotification = (notification) => {
        return (_jsx(NotificationItem, { notification: notification }, notification.id));
    };
    // Extract nested ternary operation into independent statement
    const renderNotificationContent = () => {
        const showRating = shouldShowSurveyNotification && !isRatingDismissed;
        if (error && notifications.length === 0 && !showRating) {
            return renderErrorState();
        }
        // Check if we have content to show (either notifications or rating notification)
        const hasContent = filteredNotifications.length > 0 || showRating;
        if (!hasContent) {
            return renderEmptyState();
        }
        return (_jsxs(_Fragment, { children: [showRating && !isSurveyLoading && (renderRatingNotification()), Object.entries(grouped).map(([group, notifs], idx) => (_jsxs(React.Fragment, { children: [notifs.length > 0 && renderGroupLabel(group, idx), notifs.map((notification) => renderNotification(notification))] }, group)))] }));
    };
    const scrollToTop = () => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        }
    };
    const getUnreadNotificationIds = () => {
        return notifications
            .filter((n) => !n.viewedDateTime &&
            n.title?.trim().toLowerCase() !==
                NOTIFICATION_TITLES.FEEDBACK.toLowerCase())
            .map((n) => n.id);
    };
    const markNotificationsAsReadLocally = () => {
        const now = new Date().toISOString();
        setNotifications((prev) => prev.map((n) => {
            // Don't mark feedback notifications as read
            if (n.title?.trim().toLowerCase() ===
                NOTIFICATION_TITLES.FEEDBACK.toLowerCase()) {
                return n;
            }
            return n.viewedDateTime ? n : { ...n, viewedDateTime: now };
        }));
        setUnreadCount(0);
    };
    const handleMarkAllAsRead = async () => {
        if (unreadCount === 0)
            return;
        const messagesIdList = getUnreadNotificationIds();
        try {
            await NotificationAPI.markNotificationAsRead(messagesIdList, undefined, platformHost);
            markNotificationsAsReadLocally();
        }
        catch (err) {
            console.error('Error marking notifications as read:', err);
            setError(getErrorMessage(err));
        }
    };
    const renderNotificationBadge = () => (_jsx(Badge, { badgeContent: unreadCount > 0 ? unreadCount : null, color: "error", overlap: "circular", anchorOrigin: { vertical: 'top', horizontal: 'right' }, sx: getBadgeStyles(theme, unreadCount), children: _jsx(NotificationsIcon, { sx: { color: getBellIconColor(), fontSize: 32 }, "data-testid": "notifications-icon" }) }));
    return (_jsxs(_Fragment, { children: [_jsxs(Box, { sx: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }, children: [_jsx(IconButton, { "aria-label": "show notifications", onClick: handleBellClick, size: "large", sx: { p: 0 }, "data-testid": "notification-icon-button", children: renderNotificationBadge() }), _jsxs(Menu, { anchorEl: anchorEl, open: open, onClose: handleClose, disableRestoreFocus: true, "data-testid": "notification-menu", anchorOrigin: {
                            vertical: 'bottom',
                            horizontal: 'right'
                        }, transformOrigin: {
                            vertical: 'top',
                            horizontal: 'right'
                        }, marginThreshold: 0, slotProps: {
                            paper: {
                                style: {
                                    width: 450,
                                    maxWidth: 450,
                                    height: 575,
                                    borderRadius: '16px',
                                    background: '#FFF',
                                    boxShadow: theme.shadows[8],
                                    padding: 0,
                                    overflow: 'visible',
                                    marginTop: 26,
                                    marginLeft: 58
                                },
                                sx: {
                                    '&::before': {
                                        content: '""',
                                        display: 'block',
                                        position: 'absolute',
                                        top: -10, // moved up 0.5px to overlap and hide seam
                                        left: tipPosition,
                                        transform: 'translateX(-50%)',
                                        width: 16, // slightly wider to smooth anti-aliasing
                                        height: 12, // taller so base overlaps menu top edge
                                        background: 'transparent',
                                        zIndex: 2,
                                        pointerEvents: 'none',
                                        // Arrow shape: pronounced rounded tip (dome) with added overlap to prevent bottom hairline
                                        WebkitClipPath: 'polygon(46% 0%, 54% 0%, 100% 100%, 0% 100%)',
                                        clipPath: 'path("M0 100% L46% 10% Q50% 0% 54% 10% L100% 100% Z")',
                                        backgroundColor: theme.palette.background.paper,
                                        // Removed boxShadow to eliminate dark hairline; optional subtle shadow could use filter if needed
                                        boxShadow: 'none'
                                    }
                                }
                            },
                            list: {
                                sx: {
                                    padding: 0
                                }
                            }
                        }, children: [_jsxs(Box, { sx: {
                                    display: 'flex',
                                    alignItems: 'center',
                                    p: 3,
                                    justifyContent: 'space-between',
                                    background: theme.palette.background.paper,
                                    borderTopLeftRadius: 18,
                                    borderTopRightRadius: 18,
                                    position: 'sticky',
                                    top: 0,
                                    zIndex: 1
                                }, children: [_jsxs(Typography, { variant: "subtitle1", sx: {
                                            fontWeight: 600,
                                            fontSize: 16,
                                            color: theme.palette.text.primary
                                        }, "data-testid": "typography-subtitle1", children: [constants.NOTIFICATIONS, ' ', unreadCount > 0 ? `(${unreadCount})` : ''] }), _jsxs(Box, { sx: {
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1
                                        }, children: [_jsx(Typography, { variant: "body2", sx: {
                                                    fontSize: 12,
                                                    color: theme.palette.text.secondary,
                                                    fontWeight: 600
                                                }, "data-testid": "typography-body2", children: constants.ONLY_SHOW_UNREAD }), _jsx(Switch, { checked: onlyUnread, onChange: (e) => setOnlyUnread(e.target.checked), size: "small", color: "primary", sx: {
                                                    ml: 0.5,
                                                    // Set CSS custom properties for dynamic colors
                                                    '--switch-bg-color': `${configState?.settings?.button_color || theme.palette.primary.main}`, // Slightly transparent background
                                                    '--switch-border-color': `rgba(255, 255, 255, 0.40)`, // Much lighter border for contrast
                                                    '--switch-hover-color': `${configState?.settings?.button_color || theme.palette.primary.main}14`, // Hover color with low opacity
                                                    '--switch-off-bg-color': '#D9D9D9', // Pure light grey background for off-state
                                                    '--switch-off-border-color': 'rgba(255, 255, 255, 0.40)' // More visible grey border
                                                }, "data-testid": "unread-only-switch" })] })] }), _jsx(Divider, { sx: {
                                    borderColor: theme.palette.divider,
                                    borderBottomWidth: '1px',
                                    mb: 0
                                }, "data-testid": "divider" }), _jsxs(Box, { "data-testid": "scroll-container", ref: scrollContainerRef, onScroll: handleScroll, sx: {
                                    height: 452,
                                    maxHeight: 452,
                                    overflowY: 'auto',
                                    background: theme.palette.background.default,
                                    px: 0,
                                    py: 0,
                                    pt: 1,
                                    pl: 1,
                                    pr: 1,
                                    position: 'relative',
                                    // Custom scrollbar styles
                                    '&::-webkit-scrollbar': {
                                        width: '6px'
                                    },
                                    '&::-webkit-scrollbar-track': {
                                        background: 'transparent'
                                    },
                                    '&::-webkit-scrollbar-thumb': {
                                        background: theme.palette.mode === 'light'
                                            ? '#E5E7EB'
                                            : '#4B5563',
                                        borderRadius: '3px',
                                        '&:hover': {
                                            background: theme.palette.mode === 'light'
                                                ? '#D1D5DB'
                                                : '#6B7280'
                                        }
                                    },
                                    // Remove scrollbar arrows (buttons)
                                    '&::-webkit-scrollbar-button': {
                                        display: 'none'
                                    },
                                    // Firefox scrollbar
                                    scrollbarWidth: 'thin',
                                    scrollbarColor: theme.palette.mode === 'light'
                                        ? '#E5E7EB transparent'
                                        : '#4B5563 transparent'
                                }, children: [renderNotificationContent(), isLoadingMore && (_jsx(Box, { sx: {
                                            display: 'flex',
                                            justifyContent: 'center',
                                            py: 2
                                        }, children: _jsx(Typography, { variant: "body2", sx: {
                                                color: theme.palette.text.secondary,
                                                fontSize: 13
                                            }, children: constants.loading_more }) }))] }), showScrollTop && (_jsx(Fab, { onClick: scrollToTop, size: "small", "data-testid": "scroll-to-top-button", sx: {
                                    position: 'absolute',
                                    bottom: 50,
                                    right: 25,
                                    backgroundColor: configState?.settings?.button_color ||
                                        theme.palette.primary.main,
                                    color: '#FFFFFF',
                                    width: 40,
                                    height: 40,
                                    boxShadow: theme.shadows[6],
                                    zIndex: 10,
                                    '&:hover': {
                                        backgroundColor: configState?.settings?.button_color ||
                                            theme.palette.primary.dark,
                                        opacity: 0.95
                                    }
                                }, "aria-label": "scroll to top", children: _jsx(KeyboardArrowUpIcon, { sx: { fontSize: 20, color: '#FFFFFF' } }) })), filteredNotifications.length > 0 && (_jsx(Divider, { sx: {
                                    borderColor: theme.palette.divider,
                                    borderBottomWidth: '1px',
                                    mt: 0,
                                    mb: 0
                                }, "data-testid": "divider" })), filteredNotifications.length > 0 ? (_jsx(PermissionGate, { action: [RBAC_ACTIONS.RBAC_UPDATE_NOTIFICATION], children: _jsxs(Box, { sx: {
                                        pt: 2.5,
                                        pl: 3,
                                        pr: 3,
                                        pb: 2.5,
                                        background: theme.palette.background.paper,
                                        borderBottomLeftRadius: 18,
                                        borderBottomRightRadius: 18,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'flex-start',
                                        height: '20px',
                                        cursor: unreadCount === 0
                                            ? 'not-allowed'
                                            : 'pointer',
                                        opacity: unreadCount === 0 ? 0.3 : 1,
                                        pointerEvents: unreadCount === 0 ? 'none' : 'auto',
                                        '&:hover': {
                                            background: unreadCount === 0
                                                ? theme.palette.background.paper
                                                : theme.palette.grey[50]
                                        },
                                        fontWeight: 700,
                                        fontSize: 16,
                                        color: configState?.settings?.button_color ||
                                            theme.palette.primary.main,
                                        gap: 0.5
                                    }, onClick: handleMarkAllAsRead, "data-testid": "clickable-box", children: [_jsx(Box, { sx: {
                                                width: 24,
                                                height: 24,
                                                display: 'inline-block',
                                                verticalAlign: 'middle',
                                                backgroundColor: configState?.settings
                                                    ?.button_color ||
                                                    theme.palette.primary.main,
                                                mask: `url(${checkdouble}) no-repeat center / contain`,
                                                WebkitMask: `url(${checkdouble}) no-repeat center / contain`
                                            } }), ' ', constants.MARK_ALL_AS_READ] }) })) : (_jsx(Box, { sx: {
                                    pt: 2,
                                    pb: 2,
                                    pl: 3,
                                    pr: 3,
                                    background: theme.palette.background.default,
                                    borderBottomLeftRadius: 18,
                                    borderBottomRightRadius: 18,
                                    minHeight: 48, // match the button height
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'flex-start'
                                }, "data-testid": "empty-footer" }))] })] }), _jsx(Sound, { type: "notification", play: play, setPlay: setPlay })] }));
};
export default NotificationModal;
